import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Login } from '@/pages/Login';
import { Pending } from '@/pages/Pending';
import { DashboardHome } from '@/pages/DashboardHome';
import { Insights } from '@/pages/Insights';
import { Audit } from '@/pages/Audit';
import { Roster } from '@/pages/Roster';
import { RosterImport } from '@/pages/RosterImport';
import { RosterBatches } from '@/pages/RosterBatches';
import { Models } from '@/pages/Models';
import { Licenses } from '@/pages/Licenses';
import { LicenseBatches } from '@/pages/LicenseBatches';
import { Support } from '@/pages/Support';
import { Users } from '@/pages/Users';
import type { Role } from '@/types';

const gate = (role: Role, el: ReactNode) => <ProtectedRoute requiredRole={role}>{el}</ProtectedRoute>;

// Admin console v2 IA (design-doc § 5, boards/Sitemap.dc.html). Auth is Cloudflare Access;
// role/status come from GET /api/me. The SPA gate is UX — admin-api enforces the real boundary.
export function Router() {
  return (
    <Routes>
      {/* Public shells — Access still fronts the whole app, these just show status. */}
      <Route path="/login" element={<Login />} />
      <Route path="/pending" element={<Pending />} />

      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardHome />} />
        <Route path="/insights" element={<Insights />} />
        <Route path="/audit" element={gate('admin', <Audit />)} />
        <Route path="/roster" element={gate('admin', <Roster />)} />
        <Route path="/roster/import" element={gate('admin', <RosterImport />)} />
        <Route path="/roster/batches" element={gate('admin', <RosterBatches />)} />
        <Route path="/models" element={gate('admin', <Models />)} />
        <Route path="/licenses" element={gate('admin', <Licenses />)} />
        <Route path="/licenses/batches" element={gate('admin', <LicenseBatches />)} />
        <Route path="/support" element={gate('support', <Support />)} />
        <Route path="/users" element={gate('super_admin', <Users />)} />
        {/* v1 URL: the hardware-SN tab moved to /roster (models → /models). */}
        <Route path="/devices" element={<Navigate to="/roster" replace />} />
      </Route>

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
